import { useEffect } from 'react';

import { getWindowSafe } from '@change-corgi/core/window';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useCountryCode } from 'src/app/shared/hooks/l10n';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { RootShareRecommendations } from '../useFetchShareRecommendations';
import { isPollingForEmptyResults, useFetchShareRecommendations } from '../useFetchShareRecommendations';

export type Recommendation = NonNullable<
	NonNullable<RootShareRecommendations['facebook'] | RootShareRecommendations['reddit']>[0]
>;
export type UseShareRecommendationsReturn = ModelHookResult<{
	shareRecommendations: RootShareRecommendations;
	shouldDisplayShareRecommendations: boolean;
	shareRecommendationsLoading: boolean;
	isShareRecommendationsEnabled: boolean;
}>;

export const EMPTY_SHARE_RECOMMENDATIONS: RootShareRecommendations = {
	facebook: [],
	reddit: [],
};

type CountBucket = '0' | '1' | '2' | '3' | '4' | '5+';
function bucketCount(n: number): CountBucket {
	if (n <= 0) return '0';
	if (n >= 5) return '5+';
	return String(n) as CountBucket;
}

export function useShareRecommendations(slug: string): UseShareRecommendationsReturn {
	const { errorReporter } = useUtilityContext();
	const countryCode = useCountryCode();
	const {
		data: {
			featureConfigs: { starterExperienceShareRecommendationsConfig },
		},
	} = useStarterDashboardV2PageData();

	const isShareRecommendationsEnabled = starterExperienceShareRecommendationsConfig?.isEnabled ?? false;

	const shareRecommendationsResult = useFetchShareRecommendations(slug, isShareRecommendationsEnabled);
	const shareRecommendations = isLoaded(shareRecommendationsResult)
		? shareRecommendationsResult.data
		: EMPTY_SHARE_RECOMMENDATIONS;
	const hasAnyShareRecommendations = !!shareRecommendations.reddit.length || !!shareRecommendations.facebook.length;
	// Empty-result polling keeps refetching in the background after the query enters `success`,
	// so we treat that window as still loading (matches the previous in-queryFn retry UX) and
	// defer terminal PageAction emission until the polling settles.
	const stillPolling = isPollingForEmptyResults(shareRecommendationsResult);
	const shareRecommendationsLoading =
		isShareRecommendationsEnabled &&
		((!isLoaded(shareRecommendationsResult) && !isError(shareRecommendationsResult)) || stillPolling);

	useEffect(() => {
		if (!isShareRecommendationsEnabled) return;
		if (stillPolling) return;
		const win = getWindowSafe();
		if (!win) return;

		const baseAttrs = {
			feature: 'recommended_groups',
			pageSection: 'starter_dashboard.home',
			pageVersion: 'v2',
			gqlOperation: 'StarterDashboardV2ShareRecommendations',
			country: countryCode.toLowerCase(),
			petitionSlug: slug,
			flowKind: 'fetch',
		};

		if (isLoaded(shareRecommendationsResult)) {
			const { data } = shareRecommendationsResult;
			const fbCount = data.facebook.length;
			const redditCount = data.reddit.length;
			const flowOutcome = fbCount === 0 && redditCount === 0 ? 'empty' : 'success';
			win.newrelic?.addPageAction('RecommendedGroupsDashboardLoaded', {
				...baseAttrs,
				flowOutcome,
				recsFbCount: fbCount,
				recsRedditCount: redditCount,
				recsFbCountBucket: bucketCount(fbCount),
				recsRedditCountBucket: bucketCount(redditCount),
			});
		} else if (isError(shareRecommendationsResult)) {
			const error =
				shareRecommendationsResult.error instanceof Error
					? shareRecommendationsResult.error
					: new Error(String(shareRecommendationsResult.error));
			win.newrelic?.addPageAction('RecommendedGroupsDashboardError', {
				...baseAttrs,
				flowOutcome: 'error',
				errorMessage: error.message,
			});
			void errorReporter.report({
				error,
				params: {
					resource: 'shareRecommendations',
					petitionSlug: slug,
					action: 'useFetchShareRecommendations',
				},
			});
		}
	}, [
		isShareRecommendationsEnabled,
		stillPolling,
		shareRecommendationsResult.status,
		shareRecommendationsResult.data,
		shareRecommendationsResult.error,
		slug,
		countryCode,
		errorReporter,
	]);

	return {
		data: {
			shareRecommendations,
			shouldDisplayShareRecommendations: hasAnyShareRecommendations,
			shareRecommendationsLoading,
			isShareRecommendationsEnabled,
		},
	};
}
